import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';
import { isExpiredToken } from '@/utils/cognito';
import { useSetRecoilState } from 'recoil';
import alertState from '@/states/alertState';
import useApiPresentCampaign from '@/hooks/api/campaign/useApiPresentCampaign';
import {
  PresentCampaignInfoRequest,
  PresentCampaignInfoResponse,
} from '@/types/api/mypage/campaign/presentCampaignInfo';
import useSetLoading from '@/hooks/useSetLoading';
import {
  PresentCampaignAcceptRequest,
  PresentCampaignAcceptResponse,
} from '@/types/api/mypage/campaign/presentCampaignAccept';

/**
 * プレゼントキャンペーン画面用のロジックフック。
 * API 呼び出しとローディング・エラー表示をまとめ、画面側では結果の扱いに集中できるようにする。
 *
 * @returns 取得・応募用のロジック関数群
 */
const usePresentCampaign = () => {
  // ローディング
  const { startLoading, finishLoading } = useSetLoading();

  // アラート
  const setAlert = useSetRecoilState(alertState);

  // 「プレゼントキャンペーン」の関連API
  const { executeGetPresentInfo, executePostPresentAccept } = useApiPresentCampaign();

  /**
   * 「プレゼントキャンペーン情報取得」の処理を実施する。
   * @returns 取得結果を返却する。
   */
  const getPresentCampaignInfo = async (
    request: PresentCampaignInfoRequest | null = null
  ): Promise<PresentCampaignInfoResponse | null> => {
    try {
      startLoading(ApiUrl.Read.PRESENT_CAMPAIGN_INFO);
      const { data } = await executeGetPresentInfo(request);
      if (!request?.present_code) {
        // 一覧取得時は API レスポンスをそのまま返し、画面側で一覧描画に使う。
        return data;
      }
      // 「プレゼントキャンペーン情報」が取得できない場合は、エラーダイアログを表示する。
      const presentCampaigns = data?.present_campaigns;
      if (!presentCampaigns || presentCampaigns.length <= 0) {
        setAlert({
          isOpen: true,
          apiType: Constants.ApiType.API_GET,
        });
        return null;
      }
      // 個別コード指定時は対象キャンペーンだけに絞り、詳細画面で扱いやすくする。
      data.present_campaigns = data.present_campaigns.filter(
        (e) => e.present_code === request?.present_code
      );
      return data;
    } catch (error: any) {
      if (isExpiredToken(error)) {
        window.location.href = url('/login');
        sessionStorage.clear();
      } else {
        // 取得失敗時は null を返し、画面側では表示中断だけ判断できるようにする。
        setAlert({
          isOpen: true,
          apiType: Constants.ApiType.API_GET,
        });
      }
    } finally {
      finishLoading(ApiUrl.Read.PRESENT_CAMPAIGN_INFO);
    }
    return null;
  };

  /**
   * 「プレゼント応募申込」の処理を実施する。
   * @returns 取得結果を返却する。
   */
  const postPresentCampaignAccept = async (
    request: PresentCampaignAcceptRequest
  ): Promise<PresentCampaignAcceptResponse | null> => {
    try {
      startLoading(ApiUrl.Write.PRESENT_CAMPAIGN_ACCEPT);
      const { data } = await executePostPresentAccept(request);
      // 応募成功時のレスポンスは完了画面や結果判定にそのまま返す。
      return data;
    } catch (error: any) {
      if (isExpiredToken(error)) {
        window.location.href = url('/login');
        sessionStorage.clear();
      } else {
        // 応募失敗時も null を返し、画面側は分岐を単純化できるようにする。
        setAlert({
          isOpen: true,
          apiType: Constants.ApiType.API_GET,
        });
      }
    } finally {
      finishLoading(ApiUrl.Write.PRESENT_CAMPAIGN_ACCEPT);
    }
    return null;
  };

  return {
    getPresentCampaignInfo,
    postPresentCampaignAccept,
  };
};

export default usePresentCampaign;
